/* eslint-disable no-irregular-whitespace */
import type { Space } from '@mail/cross-sizes-utils';
import { Icon28BlockOutline } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { type ReactElement, createRef, PureComponent } from 'react';
import { connect } from 'react-redux';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { overquotaNewCommunication } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewCommunication';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { DisabledFeature } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.types';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { createGaSender } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';
import { scrollLock, scrollUnlock } from 'reactApp/utils/scrollLock';

import styles from './DisabledFeatureDialog.css';

const sendGa = createGaSender('overqouta');

const getTitle = (disabledFeature) => {
    let variablePart = '';
    if (overquotaNewCommunication) {
        switch (disabledFeature) {
            case DisabledFeature.publish:
                variablePart = 'Делиться файлами';
                break;
            case DisabledFeature.newFile:
                variablePart = 'Создать файл';
                break;
            case DisabledFeature.newFolder:
                variablePart = 'Создать папку';
                break;
            case DisabledFeature.createAlbum:
                variablePart = 'Создать альбом';
                break;
            default:
                variablePart = 'Создать папку';
                break;
        }

        return `${variablePart} пока нельзя`;
    }

    switch (disabledFeature) {
        case DisabledFeature.publish:
            variablePart = 'публичного доступа';
            break;
        case DisabledFeature.newFile:
            variablePart = 'создания новых файлов';
            break;
        case DisabledFeature.createAlbum:
            variablePart = 'создания альбома';
            break;
        default:
            variablePart = 'создания новых папок';
            break;
    }

    return `Функция ${variablePart} отключена`;
};

export interface Props {
    disabledFeature: DisabledFeature;
    overquota: Space;
    onClose();
    onBuy();
    isBizUser: boolean;
}

const mapStateToProps = (state): { overquota: Space } => {
    const overquota = UserQuotaSelectors.getOverQuota(state);

    return {
        overquota,
    };
};

class DisabledFeatureDialogComponent extends PureComponent<Props> {
    private contentRef = createRef<HTMLDivElement>();

    public static defaultProps = {
        onClose: noop,
        onBuy: noop,
    };

    public componentDidMount() {
        const { disabledFeature } = this.props;

        scrollLock(this.contentRef.current);

        sendGa(disabledFeature, 'show');
    }

    public componentWillUnmount(): void {
        scrollUnlock(this.contentRef.current);
    }

    private handleClose = () => {
        const { disabledFeature } = this.props;

        sendGa(disabledFeature, 'close-click', null, true);

        this.props.onClose();
    };

    private handleBuy = () => {
        const { disabledFeature } = this.props;

        sendGa(disabledFeature, 'buy-click', null, true);

        this.props.onBuy();
    };

    private renderFooter = () => {
        return (
            <div className={styles.footer}>
                <div className={styles.buttonWrapper}>
                    <Button
                        className={styles.button}
                        theme="vk"
                        primary={overquotaNewCommunication}
                        error={!overquotaNewCommunication}
                        middle
                        data-name="action"
                        onClick={this.handleBuy}
                        sizeMode={IS_PHONE_BROWSER ? ButtonSizeMode.extraBig : ButtonSizeMode.small}
                    >
                        {overquotaNewCommunication ? 'Снять ограничение' : 'Купить дополнительное место'}
                    </Button>
                </div>
            </div>
        );
    };

    private renderDescription = () => {
        const { disabledFeature, overquota } = this.props;

        if (overquotaNewCommunication) {
            let feature = '';
            switch (disabledFeature) {
                case DisabledFeature.publish:
                    feature = 'снова делиться файлами';
                    break;
                case DisabledFeature.newFile:
                    feature = 'создавать файлы';
                    break;
                case DisabledFeature.newFolder:
                    feature = 'создавать папки';
                    break;
                case DisabledFeature.createAlbum:
                    feature = 'создавать альбомы';
                    break;
            }
            return (
                <>
                    Единое пространство Облака и Почты переполнено на <span>{overquota.value}</span> — увеличьте его с подпиской Mail Space,
                    чтобы {feature}
                </>
            );
        }

        return (
            <>
                Объем вашего Облака превышен на <strong>{overquota.value}</strong>. Доступ к вашим файлам ограничен, но скачивание
                и удаление остаются доступными.
            </>
        );
    };

    private renderRecommend = () => {
        const { isBizUser } = this.props;

        if (isBizUser) {
            return 'Обратитесь к администратору домена или удалите лишние файлы, освободив место в\u00A0Облаке.';
        }

        return 'Обновите тариф или удалите лишние файлы в\u00A0Облаке.';
    };

    private renderContent = () => {
        const { disabledFeature, isBizUser } = this.props;

        return (
            <>
                <div className={classNames({ [styles.content]: true, [styles.content_scrollable]: true })} ref={this.contentRef}>
                    <div className={styles.iconWrapper}>
                        <Icon28BlockOutline />
                    </div>
                    <div className={styles.title}>{getTitle(disabledFeature)}</div>
                    <div className={styles.description}>{this.renderDescription()}</div>
                    {!overquotaNewCommunication && <div className={styles.recommend}>{this.renderRecommend()}</div>}
                </div>
                {!isBizUser && this.renderFooter()}
            </>
        );
    };

    public render(): ReactElement | null {
        if (IS_PHONE_BROWSER) {
            return (
                <MobileDialog id="disabledFeature-dialog" onClose={this.handleClose} closeOnDimmerClick={true} mod="confirm" topmost>
                    <div className={classNames(styles.root_phone, styles.rebranding)}>{this.renderContent()}</div>
                </MobileDialog>
            );
        }

        return (
            <Dialog
                open
                onCancel={this.handleClose}
                disableScrollOnBody={false}
                id="disabledFeature-dialog"
                className={classNames(styles.root, styles.rebranding)}
                size={'tiny'}
            >
                <Content isModal isLoading={false} hasError={false} scrolling>
                    {this.renderContent()}
                </Content>
            </Dialog>
        );
    }
}

export const DisabledFeatureDialog = connect(mapStateToProps)(DisabledFeatureDialogComponent);
